import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import _ from 'lodash'
import {
  Stack,
  Spacer,
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
  Input as ChakraInput,
} from '@chakra-ui/react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import type {
  DateFilterValue,
  InputFormProps as Props,
} from '../../../interfaces'
import { getFieldsMapping } from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'

type CustomInputProps = React.HTMLProps<HTMLInputElement> & {
  handleValueChange?: (value: DateFilterValue) => void
  isCustomRange?: boolean
  onDirty?: () => void
}

const parseRangeInputValue = (
  rawValue: string | undefined,
): { from: Date; to: Date } | null => {
  if (!rawValue) {
    return null
  }

  const [startRaw, endRaw] = rawValue.split(/\s*-\s*/, 2)
  if (!startRaw?.trim() || !endRaw?.trim()) {
    return null
  }

  const startDate = new Date(startRaw.trim())
  const endDate = new Date(endRaw.trim())

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null
  }

  return { from: startDate, to: endDate }
}

const CustomInput = (
  inputProps: CustomInputProps,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement => {
  const {
    value,
    onClick,
    onChange,
    onBlur,
    onKeyDown,
    handleValueChange,
    isCustomRange,
    onDirty,
  } = inputProps

  const commitManualRange = (): void => {
    if (!isCustomRange || !handleValueChange) {
      return
    }

    const rangeValue = parseRangeInputValue(String(value ?? ''))
    if (!rangeValue) {
      return
    }

    handleValueChange({
      from: rangeValue.from.toISOString(),
      to: rangeValue.to.toISOString(),
    })
    onDirty?.()
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    commitManualRange()
    onBlur?.(event)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      commitManualRange()
    }
    onKeyDown?.(event)
  }
  return (
    <ChakraInput
      bgColor="white"
      value={value}
      onClick={onClick}
      onChange={onChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="Pick a date"
      ref={ref}
    />
  )
}

const DateInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
    onClose,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)
  const formRef = useRef<HTMLDivElement>(null)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const fullWidthForm = useMemo(
    () =>
      ['LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM_RANGE'].includes(
        expression.operator,
      ),
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: DateFilterValue) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    (initialFocusRef as any).current?.input?.focus()
  }, [initialFocusRef, expression.operator])

  // Handle clicks outside the form and datepicker calendar to close the popover
  useEffect(() => {
    if (!onClose) {
      return
    }

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node

      // Check if click is inside the form container
      if (formRef.current?.contains(target)) {
        return
      }

      // Check if click is inside the datepicker calendar (rendered in portal)
      const datepickerCalendar = document.querySelector(
        '.react-datepicker__portal, .react-datepicker',
      )
      if (datepickerCalendar?.contains(target)) {
        return
      }

      // Check if click is inside the popover content
      const popoverContent = document.querySelector('[data-popover-content]')
      if (popoverContent?.contains(target)) {
        return
      }

      // Click is outside, close the popover
      onClose()
    }

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  return (
    <ChakraFormControl
      ref={formRef}
      width="full"
      isInvalid={error !== null && dirty}
    >
      <VStack width="full" spacing="0.5em">
        <Stack
          alignItems="stretch"
          width="full"
          flexDirection={fullWidthForm ? 'column' : 'row'}
          spacing={fullWidthForm ? '0.5em' : '0'}
        >
          <OperatorMenu
            width={fullWidthForm ? 'full' : '45%'}
            expression={expression}
            handleOperatorChange={handleOperatorChange}
          />

          {!fullWidthForm && <Spacer />}

          {['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(
            expression.operator,
          ) && (
            <Box minWidth="45%" maxWidth="45%">
              <DatePicker
                ref={initialFocusRef as RefObject<any>}
                selected={
                  new Date((expression.value as { exact: string }).exact)
                }
                customInput={React.createElement(React.forwardRef(CustomInput))}
                onChange={(newDate: Date) => {
                  const newValue = {
                    exact: newDate.toISOString(),
                  }
                  handleValueChange(newValue)
                  setDirty(true)
                }}
              />
            </Box>
          )}

          {['CUSTOM_RANGE'].includes(expression.operator) && (
            <Box width="full">
              <DatePicker
                ref={initialFocusRef as RefObject<any>}
                selectsRange={true}
                startDate={
                  (
                    expression.value as {
                      to: string
                      from: string
                    }
                  ).from
                    ? new Date(
                        (
                          expression.value as {
                            to: string
                            from: string
                          }
                        ).from,
                      )
                    : null
                }
                endDate={
                  (
                    expression.value as {
                      to: string
                      from: string
                    }
                  ).to
                    ? new Date(
                        (
                          expression.value as {
                            to: string
                            from: string
                          }
                        ).to,
                      )
                    : null
                }
                onChange={update => {
                  if (Array.isArray(update)) {
                    handleValueChange({
                      from: update[0] ? update[0].toISOString() : '',
                      to: update[1] ? update[1].toISOString() : '',
                    })
                    setDirty(true)
                  }
                }}
                customInput={React.createElement(React.forwardRef(CustomInput), {
                  handleValueChange,
                  isCustomRange: true,
                  onDirty: () => setDirty(true),
                })}
              />
            </Box>
          )}
        </Stack>

        <VStack width="full" alignItems={fullWidthForm ? 'start' : 'end'}>
          <ChakraFormErrorMessage
            marginBottom="-2.0408em"
            maxWidth={fullWidthForm ? 'full' : '45%'}
          >
            {error}
          </ChakraFormErrorMessage>
        </VStack>
      </VStack>
    </ChakraFormControl>
  )
}

export default DateInputForm
